/**
 * The portal's tiny in-house i18n layer — no vue-i18n, no dependency at all
 * (see CLAUDE.md's dependency budget). A nested message object per locale, a
 * `t()` that walks a dot path and interpolates `{{name}}` tokens, and a
 * locale store that persists the choice and drives `@fcms/ui`'s
 * `setLocale` (which flips `<html lang dir>` and therefore every logical
 * property in the design system).
 *
 * Every user-facing string in this app MUST go through `t()`. A literal
 * English string in a template is a bug in a Thaana-first product — there is
 * no linter for that here, so it is a code-review discipline instead.
 */
import { computed, type ComputedRef, ref } from 'vue'
import { setLocale as setUiLocale, type UiLocale } from '@fcms/ui'
import dv from './messages/dv'
import en, { type MessageTree } from './messages/en'

export type PortalLocale = UiLocale

const STORAGE_KEY = 'fcms.portal.locale'

const messages: Record<PortalLocale, MessageTree> = { en, dv: dv as MessageTree }

function readStored(): PortalLocale | null {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw === 'en' || raw === 'dv' ? raw : null
  } catch {
    return null
  }
}

function writeStored(value: PortalLocale): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(STORAGE_KEY, value)
  } catch {
    /* Safari private mode — losing persistence is tolerable */
  }
}

// Dhivehi is the product default (see apps/portal's brief: "Dhivehi (dv) is
// the DEFAULT locale; English is a toggle"). Only an explicit prior choice
// overrides that — there is no `prefers-language` browser signal worth
// trusting the way `prefers-color-scheme` is trusted for theme.
const portalLocale = ref<PortalLocale>(readStored() ?? 'dv')

/** Call once, before mount, so the very first paint is in the right script. */
export function initLocale(): void {
  setUiLocale(portalLocale.value)
}

export function setPortalLocale(value: PortalLocale): void {
  portalLocale.value = value
  writeStored(value)
  setUiLocale(value)
}

function resolvePath(tree: unknown, path: string[]): unknown {
  let node: unknown = tree
  for (const segment of path) {
    if (typeof node !== 'object' || node === null) return undefined
    node = (node as Record<string, unknown>)[segment]
  }
  return node
}

function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key: string) => {
    const value = params[key]
    return value === undefined ? match : String(value)
  })
}

/**
 * Translates a dot-path key, e.g. `t('queue.waitingAhead', { count: 3 })`.
 * Falls back to English, then to the raw key, so a missing translation is
 * visibly a placeholder rather than a blank label — and is greppable in a
 * screenshot review.
 */
export function t(key: string, params?: Record<string, string | number>): string {
  const path = key.split('.')
  const own = resolvePath(messages[portalLocale.value], path)
  if (typeof own === 'string') return interpolate(own, params)
  const fallback = resolvePath(messages.en, path)
  if (typeof fallback === 'string') return interpolate(fallback, params)
  return key
}

export function useI18n(): {
  t: typeof t
  locale: ComputedRef<PortalLocale>
  setLocale: typeof setPortalLocale
} {
  return {
    t,
    locale: computed(() => portalLocale.value),
    setLocale: setPortalLocale,
  }
}
